/**
 * Which signed-out destinations survive sign-in.
 *
 * A member who taps a webinar reminder while their session has expired lands on the
 * sign-in screen; without this they would be dropped on the dashboard afterwards and
 * have to find the notification again. Only destinations that are meaningful to
 * arrive at directly are carried through — the rest resolve to the default, because a
 * `returnTo` is a redirect target and an open-ended one is a redirect vulnerability.
 *
 * One module rather than a conditional inside `LoginScreen`, so the list is a testable
 * fact instead of a branch nobody can see.
 */

import { ACCOUNT_DELETION_PATH } from "~/lib/account-deletion";

/**
 * `"/events/"` keeps its trailing slash on purpose: a specific webinar is worth
 * returning to, the calendar behind it is not — it is one tap from the tab bar.
 */
const RETURN_TO_PREFIXES: readonly string[] = [
  "/events/",
  "/notifications",
  ACCOUNT_DELETION_PATH,
];

function covers(prefix: string, pathname: string): boolean {
  if (pathname === prefix) return true;
  // Segment-aware, so `/notifications` never claims `/notifications-archive`.
  return pathname.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`);
}

/** The path itself when it is worth coming back to, otherwise undefined. */
export function returnToForPath(pathname: string): string | undefined {
  return RETURN_TO_PREFIXES.some((prefix) => covers(prefix, pathname))
    ? pathname
    : undefined;
}
